import React, { useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, TextInput, TouchableOpacity,
  ActivityIndicator, ScrollView, KeyboardAvoidingView, Platform,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api, apiPost } from '../../../src/api/client';
import { useTheme } from '../../../src/context/ThemeContext';
import { useNetwork } from '../../../src/context/NetworkContext';
import { useToast } from '../../../src/components/Toast';
import ContentContainer from '../../../src/components/ContentContainer';
import InlineMessage from '../../../src/components/InlineMessage';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import type { ColorTheme } from '../../../src/utils/theme';
import { rs, fs } from '../../../src/utils/responsive';
import PageFooter from '../../../src/components/PageFooter';

interface CartItem { id: string; qty: number }
interface Product { id: string; name: string; priceInCents: number }
interface PaymentMethod { id: string; stripePaymentMethodId: string; brand: string | null; last4: string | null; isDefault: boolean }

// Mirror of backend calculateShippingCents in backend/src/routes/hardware-shop.ts.
// Backend re-derives the authoritative value; this is for display only.
// 4 controllers fit per USPS Priority Medium Flat Rate Box.
const calcShippingCents = (totalQty: number) => Math.ceil(totalQty / 4) * 2000;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

export default function Checkout() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const { isOffline } = useNetwork();
  const router = useRouter();
  const { cart: cartParam } = useLocalSearchParams<{ cart: string }>();

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPM, setSelectedPM] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [shippingName, setShippingName] = useState('');
  const [shippingLine1, setShippingLine1] = useState('');
  const [shippingLine2, setShippingLine2] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingState, setShippingState] = useState('');
  const [shippingZip, setShippingZip] = useState('');

  useEffect(() => {
    try {
      if (cartParam) setCartItems(JSON.parse(decodeURIComponent(cartParam)));
    } catch { /* invalid cart param */ }

    Promise.all([
      api<Product[]>('/api/hardware/products'),
      api<PaymentMethod[]>('/api/payments/methods'),
    ]).then(([prods, pms]) => {
      setProducts(prods);
      setPaymentMethods(pms);
      const defaultPM = pms.find(p => p.isDefault) || pms[0];
      if (defaultPM) setSelectedPM(defaultPM.stripePaymentMethodId);
    }).catch((err: any) => {
      if (!isOffline) {
        toast.show({ type: 'error', title: 'Couldn’t load checkout', message: err?.message ?? 'Try again in a moment.' });
      }
    }).finally(() => setLoading(false));
  }, []);

  const subtotalCents = cartItems.reduce((sum, item) => {
    const p = products.find(pr => pr.id === item.id);
    return sum + (p ? p.priceInCents * item.qty : 0);
  }, 0);
  const totalQty = cartItems.reduce((sum, item) => sum + item.qty, 0);
  const shippingCents = totalQty > 0 ? calcShippingCents(totalQty) : 0;
  const grandTotalCents = subtotalCents + shippingCents;
  const boxCount = Math.ceil(totalQty / 4);
  const shippingTierLabel =
    totalQty === 0 ? '' :
    `USPS Priority Medium Flat Rate × ${boxCount}`;

  const handlePlaceOrder = async () => {
    if (!shippingName || !shippingLine1 || !shippingCity || !shippingState || !shippingZip) {
      setError('Please fill in all required shipping fields');
      return;
    }
    if (!selectedPM) {
      setError('Please add a payment method first');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const result = await apiPost<{ id: string; status: string }>('/api/hardware/orders', {
        items: cartItems.map(i => ({ productId: i.id, quantity: i.qty })),
        shippingName, shippingLine1, shippingLine2: shippingLine2 || undefined,
        shippingCity, shippingState, shippingZip,
        paymentMethodId: selectedPM,
      });
      router.replace(`/owner/shop/orders/${result.id}?fromCheckout=1` as never);
    } catch (err: any) {
      setError(err.message || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <ContentContainer role="owner">
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      </ContentContainer>
    );
  }

  return (
    <ContentContainer role="owner" footer={false}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: rs(40) }}>
          <Text style={styles.sectionTitle}>Order Summary</Text>
          <View style={styles.card}>
            {cartItems.map(item => {
              const p = products.find(pr => pr.id === item.id);
              if (!p) return null;
              return (
                <View key={item.id} style={styles.itemRow}>
                  <Text style={styles.itemName}>{p.name} x{item.qty}</Text>
                  <Text style={styles.itemPrice}>${((p.priceInCents * item.qty) / 100).toFixed(2)}</Text>
                </View>
              );
            })}
            <View style={[styles.itemRow, styles.subtotalRow]}>
              <Text style={styles.subtotalLabel}>Subtotal</Text>
              <Text style={styles.subtotalValue}>${(subtotalCents / 100).toFixed(2)}</Text>
            </View>
            <View style={styles.itemRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.subtotalLabel}>Shipping</Text>
                {!!shippingTierLabel && <Text style={styles.shippingHint}>{shippingTierLabel}</Text>}
              </View>
              <Text style={styles.subtotalValue}>${(shippingCents / 100).toFixed(2)}</Text>
            </View>
            <View style={[styles.itemRow, styles.totalRow]}>
              <Text style={styles.totalLabel}>Total</Text>
              <Text style={styles.totalPrice}>${(grandTotalCents / 100).toFixed(2)}</Text>
            </View>
          </View>

          <Text style={styles.sectionTitle}>Shipping Address</Text>
          <View style={styles.card}>
            <TextInput style={styles.input} placeholder="Full Name" placeholderTextColor={colors.textLight}
              value={shippingName} onChangeText={setShippingName} />
            <TextInput style={styles.input} placeholder="Address Line 1" placeholderTextColor={colors.textLight}
              value={shippingLine1} onChangeText={setShippingLine1} />
            <TextInput style={styles.input} placeholder="Address Line 2 (optional)" placeholderTextColor={colors.textLight}
              value={shippingLine2} onChangeText={setShippingLine2} />
            <View style={styles.row}>
              <TextInput style={[styles.input, { flex: 2 }]} placeholder="City" placeholderTextColor={colors.textLight}
                value={shippingCity} onChangeText={setShippingCity} />
              <TextInput style={[styles.input, { flex: 1 }]} placeholder="State" placeholderTextColor={colors.textLight}
                value={shippingState} onChangeText={setShippingState} autoCapitalize="characters" maxLength={2} />
            </View>
            <TextInput style={[styles.input, { maxWidth: 150 }]} placeholder="ZIP Code" placeholderTextColor={colors.textLight}
              value={shippingZip} onChangeText={setShippingZip} keyboardType="number-pad" maxLength={10} />
          </View>

          <Text style={styles.sectionTitle}>Payment Method</Text>
          <View style={styles.card}>
            {paymentMethods.length === 0 ? (
              <TouchableOpacity style={styles.addPMBtn} onPress={() => router.push('/payment-methods' as never)}>
                <Ionicons name="card-outline" size={20} color={colors.primary} />
                <Text style={styles.addPMText}>Add a payment method</Text>
              </TouchableOpacity>
            ) : (
              paymentMethods.map(pm => (
                <TouchableOpacity
                  key={pm.id}
                  style={[styles.pmRow, pm.stripePaymentMethodId === selectedPM && styles.pmSelected]}
                  onPress={() => setSelectedPM(pm.stripePaymentMethodId)}
                >
                  <Ionicons name="card" size={20} color={pm.stripePaymentMethodId === selectedPM ? colors.primary : colors.textSecondary} />
                  <Text style={styles.pmText}>{(pm.brand || 'Card').toUpperCase()} •••• {pm.last4}</Text>
                  {pm.stripePaymentMethodId === selectedPM && (
                    <Ionicons name="checkmark-circle" size={20} color={colors.primary} />
                  )}
                </TouchableOpacity>
              ))
            )}
          </View>

          {error && <InlineMessage type="error" message={error} visible onDismiss={() => setError(null)} />}

          <TouchableOpacity
            style={[styles.placeOrderBtn, submitting && { opacity: 0.6 }]}
            onPress={handlePlaceOrder}
            disabled={submitting}
          >
            {submitting ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <>
                <Ionicons name="lock-closed" size={18} color="#fff" />
                <Text style={styles.placeOrderText}>Place Order — ${(grandTotalCents / 100).toFixed(2)}</Text>
              </>
            )}
          </TouchableOpacity>
          <PageFooter />
        </ScrollView>
      </KeyboardAvoidingView>
    </ContentContainer>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, padding: spacing.md },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.sm, marginTop: spacing.md },
  card: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    borderWidth: 1, borderColor: c.borderLight,
  },
  itemRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6 },
  itemName: { fontSize: fontSize.sm, color: c.text },
  itemPrice: { fontSize: fontSize.sm, color: c.text, fontWeight: fontWeight.medium },
  subtotalRow: { borderTopWidth: 1, borderTopColor: c.borderLight, marginTop: spacing.xs, paddingTop: spacing.sm },
  subtotalLabel: { fontSize: fontSize.sm, color: c.textSecondary },
  subtotalValue: { fontSize: fontSize.sm, color: c.text, fontWeight: fontWeight.medium },
  shippingHint: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  totalRow: { borderTopWidth: 1, borderTopColor: c.borderLight, marginTop: spacing.xs, paddingTop: spacing.sm },
  totalLabel: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  totalPrice: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.primary },
  input: {
    borderWidth: 1, borderColor: c.borderLight, borderRadius: borderRadius.sm,
    padding: rs(12), fontSize: fs(14), color: c.text, backgroundColor: c.surface, marginBottom: spacing.sm,
  },
  row: { flexDirection: 'row', gap: spacing.sm },
  addPMBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.sm },
  addPMText: { color: c.primary, fontSize: fontSize.sm, fontWeight: fontWeight.medium },
  pmRow: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    paddingVertical: spacing.sm, paddingHorizontal: spacing.xs,
    borderRadius: borderRadius.sm,
  },
  pmSelected: { backgroundColor: c.primaryLight || c.background },
  pmText: { fontSize: fontSize.sm, color: c.text, flex: 1 },
  placeOrderBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.sm,
    backgroundColor: c.primary, paddingVertical: rs(14), borderRadius: borderRadius.md, marginTop: spacing.lg,
  },
  placeOrderText: { color: '#fff', fontWeight: fontWeight.bold, fontSize: fontSize.md },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
